import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { guestApi } from '@/api/guest';

interface Form {
  title: string;
  description: string;
  // backend MaintenancePriority = low/normal/high/urgent ('medium' -> 422).
  priority: 'low' | 'normal' | 'high' | 'urgent';
}

export function GuestMaintenancePage() {
  const { t } = useTranslation();
  const { leaseId } = useParams<{ leaseId: string }>();
  const id = Number(leaseId);
  const info = useQuery({
    queryKey: ['guest-maintenance', id],
    queryFn: () => guestApi.maintenanceInfo(id),
    retry: false,
  });
  const { register, handleSubmit } = useForm<Form>({ defaultValues: { priority: 'normal' } });
  const [photos, setPhotos] = useState<File[]>([]);
  const create = useMutation({
    mutationFn: (form: FormData) => guestApi.createMaintenance(id, form),
  });

  const onSubmit = handleSubmit(async (data) => {
    const form = new FormData();
    form.append('title', data.title);
    form.append('description', data.description);
    form.append('priority', data.priority);
    photos.forEach((p) => form.append('photos[]', p));
    await create.mutateAsync(form);
  });

  if (info.isLoading) return <Spinner />;
  if (info.error) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Card title={t('errors.link_expired')}>
          <p className="text-sm text-slate-600">{t('errors.link_expired_body')}</p>
        </Card>
      </div>
    );
  }

  if (create.isSuccess) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Card title={t('maintenance.submitted')}>
          <p className="text-sm text-green-700">{t('maintenance.submitted_body')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <Card title={t('maintenance.new_request')}>
        <p className="mb-3 text-sm text-slate-600">{info.data?.property_address}</p>
        <form onSubmit={onSubmit} className="space-y-3">
          <Input label={t('maintenance.title')} {...register('title', { required: true })} />
          <label className="block text-sm">
            {t('maintenance.description')}
            <textarea
              {...register('description', { required: true })}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            {t('maintenance.priority')}
            <select {...register('priority')} className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2">
              <option value="low">{t('maintenance.priority_values.low')}</option>
              <option value="normal">{t('maintenance.priority_values.normal')}</option>
              <option value="high">{t('maintenance.priority_values.high')}</option>
              <option value="urgent">{t('maintenance.priority_values.urgent')}</option>
            </select>
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
          />
          <Button type="submit" className="w-full" loading={create.isPending}>
            {t('common.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
